import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { Link } from 'react-router';
import { healthBandOf, toScorePercentage } from './contentHealth.utils';
import { HEALTH_METER_FILL_CLASSES } from './HealthScoreCell';
import { SkeletonBlock } from './BrandPresenceSkeleton';
import { BP_TEXT_BUTTON_SMALL_CLASSES } from './brandPresenceClasses';

/** How many rows a list shows before "Show all". */
export const METER_LIST_INITIAL_ROWS = 8;

export type ContentHealthMeterRow = {
  id: string;
  /** What the row is about, as rendered. */
  name: React.ReactNode;
  /** How many pages the row counts. */
  count: number;
  /** The row's health, 0–1; the meter is empty without one. */
  score?: number;
  /** Where the row leads; a row without a target is not a link. */
  to?: string;
  /** Where the link leads, read out after the row's own text: "Show in Content Inventory". */
  linkHint?: string;
};

function MeterRowBody({ row, countNoun, singularCountNoun }: { row: ContentHealthMeterRow; countNoun: string; singularCountNoun: string }) {
  const percentage = toScorePercentage(row.score);
  const band = row.score === undefined ? undefined : healthBandOf(row.score);

  return (
    <>
      <span className="flex min-w-0 items-baseline gap-2">
        <span className="min-w-0 truncate text-sm font-semibold text-slate-800">{row.name}</span>
        <span className="shrink-0 text-xs tabular-nums text-slate-500">{`${row.count} ${row.count === 1 ? singularCountNoun : countNoun}`}</span>
      </span>
      <span className="text-right text-sm font-bold tabular-nums text-slate-900">{percentage === undefined ? '—' : `${percentage}%`}</span>
      <span aria-hidden="true" className="col-span-2 block h-1.5 overflow-hidden rounded-full bg-slate-100">
        {band && percentage !== undefined ? (
          // The width is the score itself, known only at runtime.
          <span
            className={`block h-full rounded-full transition-[width] duration-700 ease-out ${HEALTH_METER_FILL_CLASSES[band]}`}
            style={{ width: `${percentage}%` }}
          />
        ) : null}
      </span>
    </>
  );
}

const ROW_GRID_SHAPE = 'grid items-center gap-x-3 gap-y-1.5 py-2.5';

/** A row: the name and count, the figure, and the meter under both. */
const ROW_GRID_CLASSES = `${ROW_GRID_SHAPE} grid-cols-[minmax(0,1fr)_auto]`;

/** A row that is a link, with a third column for its arrow. */
const LINK_ROW_GRID_CLASSES = `${ROW_GRID_SHAPE} grid-cols-[minmax(0,1fr)_auto_auto]`;

/**
 * A ranked list of health figures: one compact row per entry with its page count, its health and a
 * meter in the health band's colour, split by hairlines. The first rows show at once; the rest open
 * with "Show all".
 */
export function ContentHealthMeterList({
  rows,
  countNoun,
  singularCountNoun,
  emptyMessage,
  loading,
}: {
  rows: ContentHealthMeterRow[];
  /** What the count counts, in the plural: "pages". */
  countNoun: string;
  /** The same in the singular: "page". */
  singularCountNoun: string;
  emptyMessage: string;
  /** Set on the first load, before there is anything to show. */
  loading?: boolean;
}) {
  const [expanded, setExpanded] = React.useState(false);
  const listId = React.useId();

  if (loading && !rows.length) {
    return (
      <ul className="m-0 list-none divide-y divide-slate-100 p-0" aria-hidden="true">
        {Array.from({ length: METER_LIST_INITIAL_ROWS }, (_unused, index) => (
          <li key={index} className={ROW_GRID_CLASSES}>
            <SkeletonBlock className={`h-4 ${index % 2 ? 'w-1/3' : 'w-1/2'}`} />
            <SkeletonBlock className="h-4 w-10" />
            <SkeletonBlock className="col-span-2 h-1.5 w-full rounded-full" />
          </li>
        ))}
      </ul>
    );
  }

  if (!rows.length) {
    return <p className="m-0 py-6 text-center text-sm text-slate-500">{emptyMessage}</p>;
  }

  const visibleRows = expanded ? rows : rows.slice(0, METER_LIST_INITIAL_ROWS);

  return (
    <div>
      <ul id={listId} className="m-0 list-none divide-y divide-slate-100 p-0">
        {visibleRows.map((row) => (
          <li key={row.id}>
            {row.to ? (
              <Link
                to={row.to}
                className={`group -mx-2 rounded-lg px-2 text-inherit no-underline transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${LINK_ROW_GRID_CLASSES}`}
              >
                <MeterRowBody row={row} countNoun={countNoun} singularCountNoun={singularCountNoun} />
                {row.linkHint ? <span className="sr-only">{row.linkHint}</span> : null}
                <FontAwesomeIcon
                  icon={faChevronRight}
                  aria-hidden="true"
                  className="col-start-3 row-span-2 row-start-1 self-center text-[0.625rem] text-slate-300 transition-colors group-hover:text-slate-500"
                />
              </Link>
            ) : (
              <div className={ROW_GRID_CLASSES}>
                <MeterRowBody row={row} countNoun={countNoun} singularCountNoun={singularCountNoun} />
              </div>
            )}
          </li>
        ))}
      </ul>
      {rows.length > METER_LIST_INITIAL_ROWS ? (
        <button
          type="button"
          className={`mt-2 ${BP_TEXT_BUTTON_SMALL_CLASSES}`}
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? 'Show fewer' : `Show all ${rows.length}`}
        </button>
      ) : null}
    </div>
  );
}
